import { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { formatDuration, formatTimeStamp, getCallDurationInSeconds } from "../../api/callData";
import { getOrderStatusStyles } from "../../utils/orderUtils";
import { useCustomer } from "../../contexts/CustomerContext";
import { useDataAccess } from "../../hooks/useDataAccess";


const OrderDetails = ({
    selectedOrder,
    searchParams,
    setSelectedOrder,
    getOrderStatus,
    handleMarkCompleted
}) => {
    // Detect if this is a Shopify order (Kronk) vs Wards order
    const isShopifyOrder = !!(selectedOrder?.order_number || selectedOrder?.line_items || (selectedOrder?.id && !selectedOrder?.metadata));
    
    // Handle both cart_data and legacy booking_info structures (for Wards)
    const bookingData = selectedOrder?.cart_data || selectedOrder?.booking_info;
    const isCartData = !!selectedOrder?.cart_data;
    
    // Review status state (only for Wards)
    const { getEffectiveCustomerId } = useCustomer();
    const { makeCustomerApiRequest } = useDataAccess();
    const [reviewStatus, setReviewStatus] = useState("Open for Review");
    const [isReviewLoading, setIsReviewLoading] = useState(false);
    const [reviewExists, setReviewExists] = useState(false);
    const [reviewTimestamp, setReviewTimestamp] = useState(null);
    const [reviewedBy, setReviewedBy] = useState(null);
    
    // Fetch review status when order changes (only for Wards)
    useEffect(() => {
        const fetchReviewStatus = async () => {
            const customerId = getEffectiveCustomerId();
            const callId = selectedOrder?.metadata?.stream_sid;
            
            // Only fetch for wards customer and non-Shopify orders
            if (customerId !== 'wards' || !callId || isShopifyOrder) {
                return;
            }
            
            try {
                const response = await makeCustomerApiRequest(
                    `orders/${callId}/review`,
                    { method: 'GET' },
                    'wards'
                );
                
                setReviewStatus(response.status || "Open for Review");
                setReviewExists(response.exists || false);
                // Store timestamp - prefer reviewed_at, fallback to created_at
                if (response.exists && (response.reviewed_at || response.created_at)) {
                    setReviewTimestamp(response.reviewed_at || response.created_at);
                } else {
                    setReviewTimestamp(null);
                }
                // Store reviewed_by information
                if (response.exists && response.reviewed_by) {
                    setReviewedBy(response.reviewed_by);
                } else {
                    setReviewedBy(null);
                }
            } catch (error) {
                // If 404, that's expected for non-existent reviews
                if (error.message?.includes('404') || error.message?.includes('Not Found')) {
                    setReviewStatus("Open for Review");
                    setReviewExists(false);
                    setReviewTimestamp(null);
                    setReviewedBy(null);
                } else {
                    console.error('Failed to fetch review status:', error);
                }
            }
        };
        
        if (selectedOrder) {
            fetchReviewStatus();
        }
    }, [selectedOrder, getEffectiveCustomerId, makeCustomerApiRequest]);
    
    // Toggle review status
    const handleToggleReview = async () => {
        const customerId = getEffectiveCustomerId();
        const callId = selectedOrder?.metadata?.stream_sid;
        
        if (customerId !== 'wards' || !callId || isReviewLoading) {
            return;
        }
        
        const newStatus = reviewStatus === "Reviewed" ? "Open for Review" : "Reviewed";
        
        // Optimistic update
        const previousStatus = reviewStatus;
        setReviewStatus(newStatus);
        setIsReviewLoading(true);
        
        try {
            const response = await makeCustomerApiRequest(
                `orders/${callId}/review`,
                {
                    method: 'PATCH',
                    body: JSON.stringify({ status: newStatus }),
                },
                'wards'
            );
            
            setReviewStatus(response.status);
            setReviewExists(response.exists || true);
            // Update timestamp after toggle
            if (response.reviewed_at || response.created_at) {
                setReviewTimestamp(response.reviewed_at || response.created_at);
            }
            // Update reviewed_by information
            if (response.reviewed_by) {
                setReviewedBy(response.reviewed_by);
            }
        } catch (error) {
            console.error('Failed to update review status:', error);
            // Revert on error
            setReviewStatus(previousStatus);
            // TODO: Show error toast/notification
        } finally {
            setIsReviewLoading(false);
        }
    };
    const handleClose = () => {
        // Add to browser history when closing order details
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('id');
        window.history.pushState({}, '', `?${newParams.toString()}`);
        setSelectedOrder(null);
    };

    if (!selectedOrder) return null;

    // Render simplified view for Shopify orders (Kronk)
    if (isShopifyOrder) {
        const formatShopifyDate = (dateString) => {
            if (!dateString) return 'N/A';
            try {
                const date = new Date(dateString);
                return date.toLocaleString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } catch (e) {
                return dateString;
            }
        };

        const getShopifyOrderStatusStyles = (status) => {
            const statusLower = status?.toLowerCase();
            if (statusLower === 'fulfilled' || statusLower === 'paid') {
                return { 
                    icon: 'material-symbols:check-circle-outline',
                    className: 'text-green-500 tag-green'
                };
            }
            if (statusLower === 'pending' || statusLower === 'open' || statusLower === 'partially fulfilled') {
                return { 
                    icon: 'carbon:incomplete-error',
                    className: 'text-yellow-600 tag-orange'
                };
            }
            if (statusLower === 'cancelled' || statusLower === 'closed') {
                return { 
                    icon: 'material-symbols:cancel-outline',
                    className: 'text-error tag-red'
                };
            }
            return { 
                icon: 'ph:question',
                className: 'text-font-secondary tag-secondary'
            };
        };

        // Determine order status for Shopify orders
        let orderStatus = 'Unknown';
        if (selectedOrder.cancelled_at) {
            orderStatus = 'Cancelled';
        } else if (selectedOrder.closed_at) {
            orderStatus = 'Closed';
        } else if (selectedOrder.fulfillment_status === 'fulfilled') {
            orderStatus = 'Fulfilled';
        } else if (selectedOrder.fulfillment_status === 'partial') {
            orderStatus = 'Partially Fulfilled';
        } else if (selectedOrder.financial_status === 'paid') {
            orderStatus = 'Paid';
        } else if (selectedOrder.financial_status === 'pending') {
            orderStatus = 'Pending Payment';
        } else if (selectedOrder.status === 'open') {
            orderStatus = 'Open';
        } else if (selectedOrder.status) {
            orderStatus = selectedOrder.status;
        }
        
        const statusStyles = getShopifyOrderStatusStyles(orderStatus);

        return (
            <div className={`
                details-container rounded-lg p-4 overflow-y-auto transition-all duration-500 ease-out scrollable
                w-full 2xl:w-1/2
                fixed 2xl:static top-12 2xl:top-0 left-0 right-0 bottom-0 2xl:inset-auto z-40
                ${selectedOrder ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'}
            `}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold flex flex-row gap-1 items-center">
                        <Icon icon="material-symbols:info-outline" className="text-2xl text-primary" />
                        Order Details
                    </h2>
                </div>

                <div className="flex flex-col gap-3">
                    <hr className="border-b-2 border-bgAccent" />
                    
                    {/* Order Summary */}
                    <div className="flex flex-col gap-2">
                        <h3 className="subheader-textbox text-sm">Order Summary</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-font-secondary">Order Number:</span>
                                <span className="text-font-primary">#{selectedOrder.order_number || selectedOrder.name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-font-secondary">Order ID:</span>
                                <span className="text-font-primary">#{selectedOrder.id}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-font-secondary">Status:</span>
                                <div className={`p-1.5 rounded text-xs flex items-center gap-0.5 text-font-primary justify-center ${statusStyles.className}`}>
                                    <Icon icon={statusStyles.icon} className="text-lg" />
                                    <span className="text-font-primary">{orderStatus}</span>
                                </div>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-font-secondary">Financial Status:</span>
                                <span className="text-font-primary capitalize">{selectedOrder.financial_status || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-font-secondary">Fulfillment Status:</span>
                                <span className="text-font-primary capitalize">{selectedOrder.fulfillment_status || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-font-secondary">Created:</span>
                                <span className="text-font-primary">{formatShopifyDate(selectedOrder.created_at)}</span>
                            </div>
                            {selectedOrder.updated_at && (
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Last Updated:</span>
                                    <span className="text-font-primary">{formatShopifyDate(selectedOrder.updated_at)}</span>
                                </div>
                            )}
                            {selectedOrder.cancelled_at && (
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Cancelled:</span>
                                    <span className="text-font-primary">{formatShopifyDate(selectedOrder.cancelled_at)}</span>
                                </div>
                            )}
                            {selectedOrder.closed_at && (
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Closed:</span>
                                    <span className="text-font-primary">{formatShopifyDate(selectedOrder.closed_at)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <hr className="border-b-2 border-bgAccent" />

                    {/* Line Items */}
                    <div className="flex flex-col gap-2">
                        <h3 className="subheader-textbox text-sm">Line Items</h3>
                        {selectedOrder.line_items && Array.isArray(selectedOrder.line_items) && selectedOrder.line_items.length > 0 ? (
                            <div className="space-y-4">
                                {selectedOrder.line_items.map((item, index) => (
                                    <div key={index} className="border-2 border-primary-50 rounded-md p-3 flex flex-col gap-3">
                                        <div className="flex items-center gap-1">
                                            <Icon icon="material-symbols:inventory-2" className="responsive-icon-sm text-primary" />
                                            <span className="font-semibold text-font-primary text-sm sm:text-base">
                                                {item.title || item.name}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Quantity:</span>
                                                <span className="text-font-primary">{item.quantity || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Price:</span>
                                                <span className="text-font-primary">${parseFloat(item.price || 0).toFixed(2)}</span>
                                            </div>
                                            {item.sku && (
                                                <div className="flex gap-2">
                                                    <span className="text-font-secondary">SKU:</span>
                                                    <span className="text-font-primary">{item.sku}</span>
                                                </div>
                                            )}
                                            {item.vendor && (
                                                <div className="flex gap-2">
                                                    <span className="text-font-secondary">Vendor:</span>
                                                    <span className="text-font-primary">{item.vendor}</span>
                                                </div>
                                            )}
                                        </div>
                                        <hr className="border-border border-b-2" />
                                        <div className="flex justify-between text-sm">
                                            <span className="text-font-secondary">Line Total:</span>
                                            <span className="font-semibold text-primary">${(parseFloat(item.price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">No line items available</span>
                            </div>
                        )}
                    </div>

                    <hr className="border-b-2 border-bgAccent" />

                    {/* Pricing Summary */}
                    <div className="flex flex-col gap-2">
                        <h3 className="subheader-textbox text-sm">Pricing Summary</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-font-secondary">Subtotal:</span>
                                <span className="text-font-primary">${parseFloat(selectedOrder.subtotal_price || 0).toFixed(2)}</span>
                            </div>
                            {parseFloat(selectedOrder.total_tax || 0) > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Tax:</span>
                                    <span className="text-font-primary">${parseFloat(selectedOrder.total_tax || 0).toFixed(2)}</span>
                                </div>
                            )}
                            {parseFloat(selectedOrder.total_discounts || 0) > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Discounts:</span>
                                    <span className="text-font-primary">-${parseFloat(selectedOrder.total_discounts || 0).toFixed(2)}</span>
                                </div>
                            )}
                            {parseFloat(selectedOrder.total_shipping_price || 0) > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Shipping:</span>
                                    <span className="text-font-primary">${parseFloat(selectedOrder.total_shipping_price || 0).toFixed(2)}</span>
                                </div>
                            )}
                            <hr className="border-bgAccent" />
                            <div className="flex justify-between">
                                <span className="text-font-secondary font-semibold">Total:</span>
                                <span className="text-font-primary font-bold text-lg">${parseFloat(selectedOrder.total_price || 0).toFixed(2)} {selectedOrder.currency || ''}</span>
                            </div>
                        </div>
                    </div>

                    <hr className="border-b-2 border-bgAccent" />

                    {/* Customer Information */}
                    <div className="flex flex-col gap-2">
                        <h3 className="subheader-textbox text-sm">Customer Information</h3>
                        {selectedOrder.customer ? (
                            <div className="space-y-2 text-sm">
                                {selectedOrder.customer.first_name && selectedOrder.customer.last_name && (
                                    <div className="flex justify-between">
                                        <span className="text-font-secondary">Name:</span>
                                        <span className="text-font-primary">{selectedOrder.customer.first_name} {selectedOrder.customer.last_name}</span>
                                    </div>
                                )}
                                {selectedOrder.customer.email && (
                                    <div className="flex justify-between">
                                        <span className="text-font-secondary">Email:</span>
                                        <span className="text-font-primary">{selectedOrder.customer.email}</span>
                                    </div>
                                )}
                                {selectedOrder.customer.phone && (
                                    <div className="flex justify-between">
                                        <span className="text-font-secondary">Phone:</span>
                                        <span className="text-font-primary">{selectedOrder.customer.phone}</span>
                                    </div>
                                )}
                                {selectedOrder.email && !selectedOrder.customer?.email && (
                                    <div className="flex justify-between">
                                        <span className="text-font-secondary">Email:</span>
                                        <span className="text-font-primary">{selectedOrder.email}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Customer information not available</span>
                            </div>
                        )}
                    </div>

                    {/* Shipping Address */}
                    {selectedOrder.shipping_address && (
                        <>
                            <hr className="border-b-2 border-bgAccent" />
                            <div className="flex flex-col gap-2">
                                <h3 className="subheader-textbox text-sm">Shipping Address</h3>
                                <div className="space-y-2 text-sm">
                                    {selectedOrder.shipping_address.first_name && selectedOrder.shipping_address.last_name && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Name:</span>
                                            <span className="text-font-primary">{selectedOrder.shipping_address.first_name} {selectedOrder.shipping_address.last_name}</span>
                                        </div>
                                    )}
                                    {selectedOrder.shipping_address.address1 && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Address:</span>
                                            <span className="text-font-primary">{selectedOrder.shipping_address.address1}</span>
                                        </div>
                                    )}
                                    {selectedOrder.shipping_address.address2 && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Address 2:</span>
                                            <span className="text-font-primary">{selectedOrder.shipping_address.address2}</span>
                                        </div>
                                    )}
                                    {(selectedOrder.shipping_address.city || selectedOrder.shipping_address.province || selectedOrder.shipping_address.zip) && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">City/State/Zip:</span>
                                            <span className="text-font-primary">
                                                {[selectedOrder.shipping_address.city, selectedOrder.shipping_address.province, selectedOrder.shipping_address.zip].filter(Boolean).join(', ')}
                                            </span>
                                        </div>
                                    )}
                                    {selectedOrder.shipping_address.country && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Country:</span>
                                            <span className="text-font-primary">{selectedOrder.shipping_address.country}</span>
                                        </div>
                                    )}
                                    {selectedOrder.shipping_address.phone && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Phone:</span>
                                            <span className="text-font-primary">{selectedOrder.shipping_address.phone}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Billing Address */}
                    {selectedOrder.billing_address && (
                        <>
                            <hr className="border-b-2 border-bgAccent" />
                            <div className="flex flex-col gap-2">
                                <h3 className="subheader-textbox text-sm">Billing Address</h3>
                                <div className="space-y-2 text-sm">
                                    {selectedOrder.billing_address.first_name && selectedOrder.billing_address.last_name && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Name:</span>
                                            <span className="text-font-primary">{selectedOrder.billing_address.first_name} {selectedOrder.billing_address.last_name}</span>
                                        </div>
                                    )}
                                    {selectedOrder.billing_address.address1 && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Address:</span>
                                            <span className="text-font-primary">{selectedOrder.billing_address.address1}</span>
                                        </div>
                                    )}
                                    {selectedOrder.billing_address.address2 && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Address 2:</span>
                                            <span className="text-font-primary">{selectedOrder.billing_address.address2}</span>
                                        </div>
                                    )}
                                    {(selectedOrder.billing_address.city || selectedOrder.billing_address.province || selectedOrder.billing_address.zip) && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">City/State/Zip:</span>
                                            <span className="text-font-primary">
                                                {[selectedOrder.billing_address.city, selectedOrder.billing_address.province, selectedOrder.billing_address.zip].filter(Boolean).join(', ')}
                                            </span>
                                        </div>
                                    )}
                                    {selectedOrder.billing_address.country && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Country:</span>
                                            <span className="text-font-primary">{selectedOrder.billing_address.country}</span>
                                        </div>
                                    )}
                                    {selectedOrder.billing_address.phone && (
                                        <div className="flex justify-between">
                                            <span className="text-font-secondary">Phone:</span>
                                            <span className="text-font-primary">{selectedOrder.billing_address.phone}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Notes */}
                    {selectedOrder.note && (
                        <>
                            <hr className="border-b-2 border-bgAccent" />
                            <div className="flex flex-col gap-2">
                                <h3 className="subheader-textbox text-sm">Notes</h3>
                                <p className="text-sm text-font-primary">{selectedOrder.note}</p>
                            </div>
                        </>
                    )}

                    {/* Tags */}
                    {selectedOrder.tags && (
                        <>
                            <hr className="border-b-2 border-bgAccent" />
                            <div className="flex flex-col gap-2">
                                <h3 className="subheader-textbox text-sm">Tags</h3>
                                <div className="flex flex-wrap gap-2">
                                    {selectedOrder.tags.split(',').map((tag, index) => (
                                        <span key={index} className="px-2 py-1 bg-primary-20 text-primary rounded text-xs">
                                            {tag.trim()}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Original Wards order details view
    return (
        <div className={`
            details-container rounded-lg p-4 overflow-y-auto transition-all duration-500 ease-out scrollable
            w-full 2xl:w-1/2
            fixed 2xl:static top-12 2xl:top-0 left-0 right-0 bottom-0 2xl:inset-auto z-40
            ${selectedOrder ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'}
        `}>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold flex flex-row gap-1 items-center">
                    <Icon icon="material-symbols:info-outline" className="text-2xl text-primary" />
                    Order Details
                </h2>
                {getEffectiveCustomerId() === 'wards' && (
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleToggleReview}
                            disabled={isReviewLoading}
                            className={`button-container shadow-none flex items-center gap-2 px-3 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed 
                                ${reviewStatus === "Reviewed" ? "text-green-500 border-green-500 bg-green-500/10" : "text-font-secondary border-primary-50 bg-primary-20"}`}
                            title={reviewStatus === "Reviewed" ? "Mark as Open for Review" : "Mark as Reviewed"}
                        >
                            <Icon 
                                icon={reviewStatus === "Reviewed" ? "material-symbols:check-circle" : "material-symbols:radio-button-unchecked"} 
                                className={`text-lg ${reviewStatus === "Reviewed" ? "text-green-500" : "text-font-secondary"}`}
                            />
                            <span className="text-sm text-font-primary">{reviewStatus}</span>
                        </button>
                        {reviewExists && reviewTimestamp && (
                            <span className="text-sm text-font-secondary">
                                Last updated: {new Date(reviewTimestamp).toLocaleString()}
                                {reviewedBy?.name && (
                                    <> by <span className="font-bold">{reviewedBy.name}</span></>
                                )}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="flex flex-col gap-3">
                <hr className="border-b-2 border-bgAccent" />
                {/* Equipment Details */}
                <div className="flex flex-col gap-2">
                    <h3 className="subheader-textbox text-sm">{isCartData ? 'Cart Items' : 'Equipment Requested'}</h3>
                    {isCartData ? (
                        // Cart data structure
                        bookingData?.full_cart?.items && Array.isArray(bookingData.full_cart.items) && bookingData.full_cart.items.length > 0 ? (
                            <div className="space-y-4">
                                {bookingData.full_cart.items.map((item, index) => (
                                    <div key={index} className="border-2 border-primary-50 rounded-md p-3 flex flex-col gap-3">
                                        <div className="flex items-center gap-1">
                                            <Icon icon="material-symbols:construction" className="responsive-icon-sm text-primary" />
                                            <span className="font-semibold text-font-primary text-sm sm:text-base">
                                                {item.name} {item.model && `(${item.model})`}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Quantity:</span>
                                                <span className="text-font-primary">{item.quantity || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Type:</span>
                                                <span className="text-font-primary capitalize">{item.type?.replace(/_/g, ' ') || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Load Type:</span>
                                                <span className="text-font-primary">{item.load_type || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Rental Days:</span>
                                                <span className="text-font-primary">{item.rental_days || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Rental Hours:</span>
                                                <span className="text-font-primary">{item.rental_hours || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Start Date:</span>
                                                <span className="text-font-primary">{item.rental_start_date || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Start Time:</span>
                                                <span className="text-font-primary">{item.rental_start_time || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Delivery/Pickup:</span>
                                                <span className="text-font-primary capitalize">{item.delivery_vs_pickup || 'N/A'}</span>
                                            </div>
                                            {item.attachments && item.attachments.length > 0 && (
                                                <div className="col-span-2 text-sm">
                                                    <span className="text-font-secondary">Attachments: </span>
                                                    <span className="text-font-primary">{item.attachments.join(', ')}</span>
                                                </div>
                                            )}
                                        </div>
                                        <hr className="border-border border-b-2" />
                                        <div className="flex justify-between text-sm">
                                            <span className="text-font-secondary">Rental Cost:</span>
                                            <span className="font-semibold text-primary">${item.rental_cost || 0}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-font-secondary">Delivery Cost:</span>
                                            <span className="font-semibold text-primary">${item.delivery_cost || 0}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Not Available</span>
                            </div>
                        )
                    ) : (
                        // Legacy booking_info structure
                        bookingData?.equipment_requested && Array.isArray(bookingData.equipment_requested) && bookingData.equipment_requested.length > 0 ? (
                            <div className="space-y-4 px-2">
                                {bookingData.equipment_requested.map((equipment, index) => (
                                    <div key={index} className="bg-bgSecondary button-container rounded-lg p-3 flex flex-col gap-3">
                                        <div className="flex items-center gap-2">
                                            <Icon icon="material-symbols:construction" className="text-primary" />
                                            <span className="font-semibold text-primary">
                                                {equipment.type} {equipment.model && `(${equipment.model})`}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Quantity:</span>
                                                <span className="text-font-primary">{equipment.quantity || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Duration:</span>
                                                <span className="text-font-primary">{equipment.duration || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">Start Date:</span>
                                                <span className="text-font-primary">{equipment.start_date || 'N/A'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="text-font-secondary">End Date:</span>
                                                <span className="text-font-primary">{equipment.end_date || 'N/A'}</span>
                                            </div>
                                            {equipment.special_requirements && (
                                                <div className="text-sm">
                                                    <span className="text-font-secondary">Special Requirements: </span>
                                                    <span className="text-font-primary">{equipment.special_requirements}</span>
                                                </div>
                                            )}
                                        </div>
                                        <hr className="border-bgAccent" />
                                        <div className="flex justify-between text-sm">
                                            <span className="text-font-secondary">Pricing:</span>
                                            {equipment.pricing_discussed && equipment.pricing_discussed.trim().length > 0 ? (
                                                <span className="font-semibold text-primary">{equipment.pricing_discussed}</span>
                                            ) : (
                                                <div className="flex flex-row gap-1 items-center text-font-secondary">
                                                    <Icon icon="material-symbols:info-outline" className="text-lg" />
                                                    <span className="text-sm">Not Available</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Not Available</span>
                            </div>
                        )
                    )}
                </div>

                {/* Order Summary */}
                <div className="flex flex-col gap-2">
                    <h3 className="subheader-textbox text-sm">Order Summary</h3>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-font-secondary">Call ID:</span>
                            <span className="text-font-primary">#{selectedOrder.metadata.stream_sid}</span>
                        </div>
                        {(isCartData ? bookingData?.summary?.cart_id : bookingData?.booking_status?.booking_id) && (
                            <div className="flex justify-between">
                                <span className="text-font-secondary">{isCartData ? 'Cart ID:' : 'Booking ID:'}</span>
                                <span className="text-font-primary">{isCartData ? bookingData.summary.cart_id : bookingData.booking_status.booking_id}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-center">
                            <span className="text-font-secondary">Status:</span>
                            <div className={`p-1.5 rounded text-xs flex items-center gap-0.5 text-font-primary justify-center ${getOrderStatusStyles(getOrderStatus(bookingData, selectedOrder.booking_resolved), selectedOrder.booking_resolved).className}`}>
                                <Icon icon={getOrderStatusStyles(getOrderStatus(bookingData, selectedOrder.booking_resolved), selectedOrder.booking_resolved).icon} className="text-lg" />
                                <span className="text-font-primary">{getOrderStatus(bookingData, selectedOrder.booking_resolved)}</span>
                            </div>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-font-secondary">Call Duration:</span>
                            <span className="text-font-primary">{formatDuration(getCallDurationInSeconds(selectedOrder))}</span>
                        </div>
                        {isCartData && bookingData?.summary && (
                            <>
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Total Items:</span>
                                    <span className="text-font-primary">{bookingData.summary.total_items}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Created:</span>
                                    <span className="text-font-primary">{new Date(bookingData.summary.created_at).toLocaleString()}</span>
                                </div>
                            </>
                        )}
                        {!isCartData && bookingData?.booking_status?.urgency && (
                            <div className="flex justify-between">
                                <span className="text-font-secondary">Urgency:</span>
                                <span className={`px-2 py-1 rounded text-xs capitalize bg-bgSecondary button-container ${
                                    bookingData.booking_status.urgency === 'high' ? 'text-error' :
                                    bookingData.booking_status.urgency === 'medium' ? 'text-yellow-600' :
                                    'text-green-500'
                                }`}>
                                    {bookingData.booking_status.urgency}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <hr className="border-b-2 border-bgAccent" />

                {/* Customer Information */}
                <div className="flex flex-col gap-2">
                    <h3 className="subheader-textbox text-sm">Customer Information</h3>
                    {isCartData ? (
                        // Cart data customer structure
                        bookingData?.full_cart?.customer_info && Object.keys(bookingData.full_cart.customer_info).length > 0 ? (
                            <div className="space-y-2 text-sm">
                                {Object.entries(bookingData.full_cart.customer_info).map(([key, value]) => (
                                    <div key={key} className="flex justify-between">
                                        <span className="text-font-secondary capitalize">{key.replace(/_/g, ' ')}:</span>
                                        <span className="text-font-primary">
                                            {value === null || value === undefined || value === ''
                                                ? 'Not specified'
                                                : typeof value === 'object'
                                                    ? JSON.stringify(value)
                                                    : typeof value === 'boolean'
                                                        ? (value ? 'Yes' : 'No')
                                                        : value.toString()
                                        }
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Customer information not provided</span>
                            </div>
                        )
                    ) : (
                        // Legacy booking_info customer structure
                        bookingData?.customer_info && Object.keys(bookingData.customer_info).length > 0 ? (
                            <div className="space-y-2 text-sm">
                                {Object.entries(bookingData.customer_info).map(([key, value]) => (
                                    <div key={key} className="flex justify-between">
                                        <span className="text-font-secondary capitalize">{key.replace(/_/g, ' ')}:</span>
                                        <span className="text-font-primary">
                                            {value === null || value === undefined
                                                ? 'Not specified'
                                                : typeof value === 'object'
                                                    ? JSON.stringify(value)
                                                    : typeof value === 'boolean'
                                                        ? (value ? 'Yes' : 'No')
                                                        : value.toString()
                                            }
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Not Available</span>
                            </div>
                        )
                    )}
                </div>

                

                <hr className="border-b-2 border-bgAccent" />

                {/* Delivery Information */}
                <div className="flex flex-col gap-2">
                    <h3 className="subheader-textbox text-sm">Delivery Information</h3>
                    {isCartData ? (
                        // Cart data delivery structure
                        bookingData?.full_cart?.delivery && Object.keys(bookingData.full_cart.delivery).length > 0 ? (
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Method:</span>
                                    <span className="text-font-primary">{bookingData.full_cart.delivery.method || 'Not specified'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-font-secondary">Total Cost:</span>
                                    <span className="text-font-primary font-semibold">${bookingData.full_cart.delivery.total_cost || 0}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-font-secondary">Bundling Applied:</span>
                                    <span className={`p-1.5 rounded text-xs text-font-primary justify-center ${bookingData.full_cart.delivery.bundling_applied ? 'tag-green' : 'tag-red'}`}>
                                        {bookingData.full_cart.delivery.bundling_applied ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                {bookingData.full_cart.delivery.bundling_details && bookingData.full_cart.delivery.bundling_details.length > 0 && (
                                    <div className="flex flex-col items-start gap-1">
                                        <span className="text-font-secondary">Bundling Details:</span>
                                        <div className="text-font-primary">
                                            {bookingData.full_cart.delivery.bundling_details.map((detail, index) => (
                                                <div key={index}>• {detail}</div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Delivery information not available</span>
                            </div>
                        )
                    ) : (
                        // Legacy booking_info delivery structure
                        bookingData?.delivery_info && Object.keys(bookingData.delivery_info).length > 0 ? (
                            <div className="space-y-2 text-sm">
                                {Object.entries(bookingData.delivery_info).map(([key, value]) => (
                                    <div key={key} className="flex justify-between">
                                        <span className="text-font-secondary capitalize">{key.replace(/_/g, ' ')}:</span>
                                        <span className="text-font-primary">
                                            {value === null || value === undefined
                                                ? 'Not specified'
                                                : typeof value === 'object'
                                                    ? JSON.stringify(value)
                                                    : typeof value === 'boolean'
                                                        ? (value ? 'Yes' : 'No')
                                                        : value.toString()
                                            }
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Not Available</span>
                            </div>
                        )
                    )}
                </div>

                <hr className="border-b-2 border-bgAccent" />

                {/* Cart Summary - Only show for cart_data */}
                {isCartData && (
                    <div className="flex flex-col gap-2">
                        <h3 className="subheader-textbox text-sm">Cart Summary</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-font-secondary">Customer Name:</span>
                                <span className="text-font-primary">{bookingData.summary.customer_name || 'Not provided'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-font-secondary">Customer Info Status:</span>
                                <span className={`p-1.5 rounded text-xs text-font-primary justify-center ${bookingData.summary.has_customer_info ? 'tag-green' : 'tag-orange'}`}>
                                    {bookingData.summary.has_customer_info ? 'Complete' : 'Incomplete'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-font-secondary">Last Updated:</span>
                                <span className="text-font-primary">{new Date(bookingData.summary.updated_at).toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Pricing Information - Only show for legacy booking_info */}
                {!isCartData && (
                    <div className="flex flex-col gap-2">
                        <h3 className="subheader-textbox text-sm">Pricing Information</h3>
                        {bookingData?.pricing_info && Object.keys(bookingData.pricing_info).length > 0 ? (
                            <div className="space-y-2 text-sm">
                                {Object.entries(bookingData.pricing_info).map(([key, value]) => (
                                    <div key={key} className="flex justify-between">
                                        <span className="text-font-secondary capitalize">{key.replace(/_/g, ' ')}:</span>
                                        <span className="text-font-primary font-semibold">
                                            {value === null || value === undefined
                                                ? 'Not specified'
                                                : typeof value === 'object'
                                                    ? JSON.stringify(value)
                                                    : typeof value === 'boolean'
                                                        ? (value ? 'Yes' : 'No')
                                                        : value.toString()
                                            }
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Not Available</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Booking Status Details - Only show for legacy booking_info */}
                {!isCartData && (
                    <div className="flex flex-col gap-2">
                        <h3 className="subheader-textbox text-sm">Next Steps</h3>
                        {bookingData?.booking_status?.next_steps ? (
                            <p className="text-sm text-font-primary">{bookingData.booking_status.next_steps}</p>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Not Available</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Additional Notes - Only show for legacy booking_info */}
                {!isCartData && (
                    <div className="flex flex-col gap-2">
                        <h3 className="subheader-textbox text-sm">Additional Notes</h3>
                        {bookingData?.additional_notes && bookingData.additional_notes.trim().length > 0 ? (
                            <p className="text-sm text-font-primary">{bookingData.additional_notes}</p>
                        ) : (
                            <div className="flex flex-row gap-1 items-center text-font-secondary">
                                <Icon icon="material-symbols:info-outline" className="text-lg" />
                                <span className="text-sm">Not Available</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderDetails;
